import { useEffect, useState } from 'react';
import { api, type Settings as SettingsType } from '../api';

const PRESETS: Record<string, { baseUrl: string; model: string }> = {
  deepseek: { baseUrl: 'https://api.deepseek.com/v1', model: 'deepseek-chat' },
  openai: { baseUrl: 'https://api.openai.com/v1', model: 'gpt-4o-mini' },
  qwen: { baseUrl: 'https://dashscope.aliyuncs.com/compatible-mode/v1', model: 'qwen-plus' },
  glm: { baseUrl: 'https://open.bigmodel.cn/api/paas/v4', model: 'glm-4-flash' },
  doubao: { baseUrl: 'https://ark.cn-beijing.volces.com/api/v3', model: 'doubao-pro-32k' },
  custom: { baseUrl: '', model: '' },
};

export default function Settings() {
  const [s, setS] = useState<SettingsType | null>(null);
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(4096);
  const [preset, setPreset] = useState('deepseek');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    void api.getSettings().then((x) => {
      setS(x);
      setBaseUrl(x.provider.baseUrl);
      setApiKey(x.provider.apiKey);
      setModel(x.provider.model);
      setTemperature(x.provider.temperature);
      setMaxTokens(x.provider.maxTokens);
      setPreset(x.provider.preset);
    });
  }, []);

  const flash = (ok: boolean, text: string) => { setMsg({ ok, text }); setTimeout(() => setMsg(null), 5000); };

  const applyPreset = (p: string) => {
    setPreset(p);
    const pr = PRESETS[p];
    if (pr && p !== 'custom') { setBaseUrl(pr.baseUrl); setModel(pr.model); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.putSettings({ preset, baseUrl, apiKey, model, temperature, maxTokens });
      setS(r);
      setApiKey(r.provider.apiKey);
      flash(true, r.gatewayEnabled ? '已保存，统一网关已启用' : '已保存（未配置 apiKey，网关未启用）');
    } catch (e) { flash(false, String((e as Error)?.message ?? e)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.testSettings(baseUrl, apiKey);
      flash(r.ok, r.message);
    } catch (e) { flash(false, String((e as Error)?.message ?? e)); }
    finally { setTesting(false); }
  };

  const changePw = async () => {
    const oldPassword = prompt('请输入当前密码');
    if (!oldPassword) return;
    const newPassword = prompt('请输入新密码（至少 6 位）');
    if (!newPassword) return;
    try { await api.changePassword(oldPassword, newPassword); flash(true, '密码已修改'); }
    catch (e) { flash(false, String((e as Error)?.message ?? e)); }
  };

  if (!s) return <div className="p-6 text-[#898b8f]">加载中…</div>;

  return (
    <div className="p-6">
      <h2 className="mb-4 text-lg font-semibold text-[#272b33]">🔑 统一 API 配置</h2>
      <div className="mb-4 rounded-lg border border-[#edeff2] bg-white p-4">
        <div className="mb-3 text-sm font-semibold text-[#272b33]">模型供应商（全局唯一，所有账户共用）</div>
        <div className="mb-3 flex flex-wrap gap-2">
          {Object.keys(PRESETS).map((k) => (
            <button key={k} onClick={() => applyPreset(k)}
              className={'rounded-md px-3 py-1 text-xs ' + (preset === k ? 'bg-[#1966ff] text-white' : 'bg-[#f0f2f7] text-[#565a60] hover:bg-[#e1e3e5]')}>
              {k === 'custom' ? '自定义' : k}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="block">
            <div className="mb-1 text-xs text-[#898b8f]">接口地址 baseUrl</div>
            <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
              className="w-full rounded-md border border-[#e1e3e5] bg-white px-3 py-1.5 text-sm text-[#272b33] focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
          </label>
          <label className="block">
            <div className="mb-1 text-xs text-[#898b8f]">API Key {apiKey.includes('****') && <span className="text-[#0bb359]">（已保存，留空不变）</span>}</div>
            <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="sk-..."
              className="w-full rounded-md border border-[#e1e3e5] bg-white px-3 py-1.5 text-sm text-[#272b33] focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
          </label>
          <label className="block">
            <div className="mb-1 text-xs text-[#898b8f]">默认模型</div>
            <input value={model} onChange={(e) => setModel(e.target.value)}
              className="w-full rounded-md border border-[#e1e3e5] bg-white px-3 py-1.5 text-sm text-[#272b33] focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
          </label>
          <label className="block">
            <div className="mb-1 text-xs text-[#898b8f]">温度 temperature（{temperature.toFixed(1)}）</div>
            <input type="range" min={0} max={1.5} step={0.1} value={temperature} onChange={(e) => setTemperature(Number(e.target.value))}
              className="mt-2 w-full accent-indigo-500" />
          </label>
          <label className="block">
            <div className="mb-1 text-xs text-[#898b8f]">max_tokens</div>
            <input type="number" value={maxTokens} onChange={(e) => setMaxTokens(Math.max(128, Math.min(32768, Number(e.target.value) || 4096)))}
              className="w-full rounded-md border border-[#e1e3e5] bg-white px-3 py-1.5 text-sm text-[#272b33] focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
          </label>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <button onClick={() => void save()} disabled={saving}
            className="rounded-md bg-[#1966ff] px-3 py-1.5 text-sm text-white hover:bg-[#3377ff] disabled:opacity-40">{saving ? '保存中…' : '保存配置'}</button>
          <button onClick={() => void test()} disabled={testing}
            className="rounded-md bg-[#f0f2f7] px-3 py-1.5 text-sm text-[#565a60] hover:bg-[#e1e3e5] disabled:opacity-40">{testing ? '测试中…' : '测试连接'}</button>
          <button onClick={() => void changePw()} className="ml-auto rounded-md bg-[#f0f2f7] px-3 py-1.5 text-xs text-[#565a60] hover:bg-[#e1e3e5]">修改管理员密码</button>
        </div>
        {msg && (
          <div className={'mt-3 rounded-md border px-3 py-2 text-xs ' + (msg.ok ? 'border-[#c9f5d7] bg-[#f0fcf4] text-[#0bb359]' : 'border-[#ffdedb] bg-[#fff1f0] text-[#fa2331]')}>{msg.text}</div>
        )}
        {s.gatewayEnabled && (
          <div className="mt-3 rounded-md border border-emerald-900/60 bg-emerald-950/30 px-3 py-2 text-xs text-[#0bb359]">
            ✅ 网关已启用 —— 客户端使用 https://xlb.tangdou2027.top/api/v1 + 账户 token 即可调用
          </div>
        )}
      </div>
    </div>
  );
}